import { Gift, Trophy, Users } from '@phosphor-icons/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useState, type CSSProperties } from 'react';

import {
  useParticipateInGiveawayMutation,
  type GiveawayDto,
  type GiveawayReaction,
} from '@entities/giveaway';
import { DealCountdown, ProductLogo, useDealRemainingMs } from '@entities/product';
import { apiErrorMessage } from '@shared/api/baseApi';
import { haptics } from '@shared/lib/telegram';
import { useLogoAccent } from '@shared/lib/useLogoAccent';
import styles from './GiveawayHero.module.css';

const REACTIONS: { id: GiveawayReaction; emoji: string; label: string }[] = [
  { id: 'LIKE', emoji: '👍', label: 'Like' },
  { id: 'LOVE', emoji: '❤️', label: 'Love' },
  { id: 'CARE', emoji: '🥰', label: 'Care' },
  { id: 'WOW', emoji: '😮', label: 'Wow' },
  { id: 'CELEBRATE', emoji: '🎉', label: 'Celebrate' },
  { id: 'FIRE', emoji: '🔥', label: 'Fire' },
];

export function GiveawayHero({
  giveaway,
  clockOffsetMs,
}: {
  giveaway: GiveawayDto;
  clockOffsetMs: number;
}) {
  const reducedMotion = useReducedMotion();
  const remaining = useDealRemainingMs(giveaway.endsAt, clockOffsetMs);
  const [choosing, setChoosing] = useState(giveaway.userReaction !== null);
  const [participate, { isLoading }] = useParticipateInGiveawayMutation();
  const [error, setError] = useState<string | null>(null);
  const accent = useLogoAccent(giveaway.product.logoUrl, '#35cd7c');
  const full = giveaway.participantCount >= giveaway.participantLimit;
  const completed = giveaway.status !== 'LIVE';

  if (!completed && remaining <= 0) return null;

  const react = async (reaction: GiveawayReaction) => {
    setError(null);
    try {
      await participate({ id: giveaway.id, reaction }).unwrap();
      haptics.notify('success');
      setChoosing(true);
    } catch (cause) {
      haptics.notify('error');
      setError(apiErrorMessage(cause, 'Could not enter the giveaway.'));
    }
  };

  return (
    <motion.section
      className={styles.hero}
      style={{
        '--banner-accent': accent.color,
        '--banner-accent-ink': accent.ink,
      } as CSSProperties}
      initial={reducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      aria-labelledby="giveaway-title"
    >
      <div className={styles.main}>
        <ProductLogo
          className={styles.product}
          src={giveaway.product.logoUrl}
          alt=""
          width={104}
          height={104}
          loading="eager"
        />
        <div className={styles.copy}>
          <span className={styles.eyebrow}>
            {completed ? <Trophy size={14} weight="fill" /> : <Gift size={14} weight="fill" />}
            {completed ? `Giveaway winner${giveaway.winners.length === 1 ? '' : 's'}` : 'Giveaway'}
          </span>
          <h2 id="giveaway-title" className={styles.name}>{giveaway.product.name}</h2>
          {!completed ? (
            <DealCountdown
              className={styles.countdown}
              endsAt={giveaway.endsAt}
              clockOffsetMs={clockOffsetMs}
            />
          ) : <strong className={styles.drawComplete}>Draw complete</strong>}
          <p className={styles.prices}>
            <s>{giveaway.product.listPrice.label}</s>
            <strong>FREE</strong>
          </p>
        </div>
      </div>

      <div className={styles.people}>
        <Users size={16} weight="fill" />
        <span><strong>{giveaway.participantCount}</strong> / {giveaway.participantLimit} participating</span>
      </div>
      <div className={styles.progress} aria-hidden>
        <span style={{ width: `${Math.min(100, giveaway.participantCount / giveaway.participantLimit * 100)}%` }} />
      </div>

      {completed ? (
        <div className={styles.winners}>
          <p>🎉 Congratulations to {giveaway.winners.length === 1 ? 'our winner' : `our ${giveaway.winners.length} winners`}!</p>
          {giveaway.winners.map((winner) => (
            <strong key={winner.position}>
              <Trophy size={16} weight="fill" />
              {winner.username
                ? `@${winner.username}`
                : winner.firstName ?? `Winner ${winner.position + 1}`}
            </strong>
          ))}
        </div>
      ) : (
      <AnimatePresence initial={false}>
        {choosing || giveaway.userReaction ? (
          <motion.div
            className={styles.reactions}
            initial={reducedMotion ? false : { opacity: 0, height: 0, y: 6 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0 }}
          >
            <p>{giveaway.userReaction ? 'You’re participating — change reaction anytime' : 'Choose your reaction'}</p>
            <div className={styles.reactionRow}>
              {REACTIONS.map((reaction) => (
                <button
                  key={reaction.id}
                  type="button"
                  className={giveaway.userReaction === reaction.id ? styles.reactionActive : styles.reaction}
                  disabled={isLoading}
                  aria-label={`${reaction.label}, ${giveaway.reactionCounts[reaction.id]} reactions`}
                  onClick={() => void react(reaction.id)}
                >
                  <span>{reaction.emoji}</span>
                  {giveaway.reactionCounts[reaction.id] > 0 ? <small>{giveaway.reactionCounts[reaction.id]}</small> : null}
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.button
            type="button"
            className={styles.cta}
            whileTap={reducedMotion ? undefined : { scale: 0.98 }}
            disabled={full}
            onClick={() => {
              haptics.tap('light');
              setChoosing(true);
            }}
          >
            {full ? 'Participation full' : 'Participate now'}
          </motion.button>
        )}
      </AnimatePresence>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
    </motion.section>
  );
}
